/**
 * ConfidenceMeter - AI Response Confidence Indicator
 *
 * Visual indicator showing confidence level of AI responses.
 * Features color-coded bars and methodology attribution.
 *
 * @module components/ConfidenceMeter
 */

import React from 'react';

import { Gauge } from 'lucide-react';

/**
 * ConfidenceMeter Component
 *
 * @param {Object} props
 * @param {number} props.confidence - Confidence value between 0-1
 * @param {string} props.methodology - Method used for confidence calculation
 */
const ConfidenceMeter = ({ confidence, methodology }) => {
  if (confidence === undefined || confidence === null) return null;

  const percent = Math.round(confidence * 100);
  const level = percent >= 80 ? 'high' : percent >= 50 ? 'medium' : 'low';

  const colors = {
    high: {
      bg: 'bg-emerald-500',
      text: 'text-emerald-600 dark:text-emerald-400',
      label: 'High Confidence',
    },
    medium: {
      bg: 'bg-amber-500',
      text: 'text-amber-600 dark:text-amber-400',
      label: 'Medium Confidence',
    },
    low: { bg: 'bg-red-500', text: 'text-red-600 dark:text-red-400', label: 'Low Confidence' },
  };

  const color = colors[level];

  return (
    <div className="flex items-center gap-2 text-xs">
      <div className="flex items-center gap-1.5">
        <Gauge className={`h-3.5 w-3.5 ${color.text}`} />
        <div className="w-16 h-1.5 bg-slate-200 dark:bg-neutral-700 rounded-full overflow-hidden">
          <div
            className={`h-full ${color.bg} transition-all duration-500`}
            style={{ width: `${percent}%` }}
          />
        </div>
        <span className={`font-medium ${color.text}`}>{percent}%</span>
      </div>
      {methodology && (
        <span className="text-slate-400 dark:text-neutral-500 text-[10px]">
          via {methodology.replace(/_/g, ' ')}
        </span>
      )}
    </div>
  );
};

export default ConfidenceMeter;
